import { EditOutlined } from "@ant-design/icons";
import { Card, Col, Modal, Row, Tooltip, Typography } from "antd";
import React, { useState } from "react";
import NotificationForm from "./NotificationForm";
import { LuAlarmCheck } from "react-icons/lu";
import {
  FaCalendarAlt,
  FaExclamationTriangle,
  FaHeartbeat,
  FaRegBell,
  FaStethoscope,
} from "react-icons/fa";
import { useCheckPageComponentAccess } from "@src/hooks/index";

const { Text } = Typography;

type CardTheme = {
  gradient: string;
  accent: string;
  soft: string;
  badge: string;
  icon: React.ReactNode;
};

const cardThemes: Record<string, CardTheme> = {
  "Daily Abnormal Screening Notification": {
    gradient: "linear-gradient(135deg, #f56565 0%, #c53030 100%)",
    accent: "#c53030",
    soft: "#fff5f5",
    badge: "#fed7d7",
    icon: <FaExclamationTriangle />,
  },
  "Hourly Abnormal Screening Notification": {
    gradient: "linear-gradient(135deg, #ed8936 0%, #c05621 100%)",
    accent: "#c05621",
    soft: "#fffaf0",
    badge: "#feebc8",
    icon: <FaHeartbeat />,
  },
  "Abnormal Screening Notification": {
    gradient: "linear-gradient(135deg, #f6ad55 0%, #dd6b20 100%)",
    accent: "#dd6b20",
    soft: "#fffaf0",
    badge: "#feebc8",
    icon: <FaStethoscope />,
  },
  "Daily Next Screening Notification": {
    gradient: "linear-gradient(135deg, #667eea 0%, #5a4fcf 100%)",
    accent: "#5a4fcf",
    soft: "#f5f3ff",
    badge: "#e0e7ff",
    icon: <FaCalendarAlt />,
  },
  "Next Screening Notification": {
    gradient: "linear-gradient(135deg, #4fd1c5 0%, #2c7a7b 100%)",
    accent: "#2c7a7b",
    soft: "#e6fffa",
    badge: "#b2f5ea",
    icon: <FaRegBell />,
  },
};

const fallbackTheme: CardTheme = {
  gradient: "linear-gradient(135deg, #718096 0%, #2d3748 100%)",
  accent: "#2d3748",
  soft: "#f7fafc",
  badge: "#e2e8f0",
  icon: <FaRegBell />,
};

interface NotificationCardProps {
  title: string;
  emails: string[];
  schedule: string;
  item?: any;
}

const NotificationCard: React.FC<NotificationCardProps> = (props) => {
  const { title, emails, schedule, item } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onClickToOpen = () => {
    setModalOpen(true);
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  const theme = cardThemes[title] || fallbackTheme;

  return (
    <div>
      <Card
        className="notification-card"
        style={{ borderLeft: `4px solid ${theme.accent}` }}
      >
        <Row
          className="card-header"
          justify="space-between"
          align="middle"
          style={{ background: theme.gradient }}
        >
          <Col>
            <div className="card-title-wrap">
              <span className="card-title-icon">{theme.icon}</span>
              <h3 className="card-title">{title}</h3>
            </div>
          </Col>
        </Row>
        <Row className="card-body">
          <Col span={24}>
            <div className="email-label-container">
              <Text className="email-label">Email IDs</Text>
              {useCheckPageComponentAccess("notification_update_action") && (
                <Tooltip title="Edit">
                  <EditOutlined
                    onClick={onClickToOpen}
                    className="notification-edit-icon"
                    style={{ color: theme.accent }}
                  />
                </Tooltip>
              )}
            </div>
            {emails.length > 0 ? (
              <div className="email-chip-list">
                {emails.map((email, index) => (
                  <span
                    key={index}
                    className="email-chip"
                    style={{
                      background: theme.badge,
                      color: theme.accent,
                      border: `1px solid ${theme.accent}22`,
                    }}
                  >
                    {email}
                  </span>
                ))}
              </div>
            ) : (
              <Text className="email-empty">No recipients configured yet</Text>
            )}
          </Col>
        </Row>
        <Row
          className="card-footer"
          align="middle"
          style={{ background: theme.soft }}
        >
          <LuAlarmCheck
            className="clock-icon"
            style={{ color: theme.accent }}
          />
          <Text className="schedule-text" style={{ color: theme.accent }}>
            {schedule}
          </Text>
        </Row>
      </Card>
      <Modal
        title={<span className="notification-form-title">{title}</span>}
        open={modalOpen}
        footer={null}
        onCancel={handleCancel}
        destroyOnClose={true}
      >
        <NotificationForm handleCancel={handleCancel} item={item} />
      </Modal>
    </div>
  );
};

export default NotificationCard;
